import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { 
  Leaf, 
  ArrowLeft, 
  ArrowRight,
  Target,
  Users,
  Award,
  Building2,
  CheckCircle,
  BarChart3,
  Calendar,
  FileText,
  Bell,
  Newspaper,
  Shield,
  Zap,
  Quote,
  Star,
  Send,
  MessageSquarePlus
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CaseStudies() {
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);
  const [publicTestimonials, setPublicTestimonials] = useState([]);
  const [formData, setFormData] = useState({
    quote: "",
    author_role: "",
    company_description: "",
    rating: 5
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem("token");
    setIsLoggedIn(!!token);
    
    // Fetch public testimonials
    fetchPublicTestimonials();
  }, []);

  const fetchPublicTestimonials = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/testimonials/public`);
      if (response.data && response.data.length > 0) {
        setPublicTestimonials(response.data);
      }
    } catch (error) {
      console.log("No public testimonials yet");
    }
  };

  const handleSubmitTestimonial = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError("");
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/testimonials`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmitSuccess(true);
      setFormData({ quote: "", author_role: "", company_description: "", rating: 5 });
      setTimeout(() => {
        setShowTestimonialForm(false);
        setSubmitSuccess(false);
      }, 3000);
    } catch (error) {
      setSubmitError(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Use public testimonials if available, otherwise use defaults
  const displayTestimonials = publicTestimonials.length > 0 ? publicTestimonials.slice(0, 3) : [
    {
      quote: "L'analyse IA nous a permis de structurer notre démarche RSE en quelques semaines. Nous avons enfin une vision claire de notre impact.",
      author_role: "Responsable Qualité & RSE",
      company_description: "PME industrielle, 120 salariés",
      author_initials: "SL",
      rating: 5
    },
    {
      quote: "Le tableau de bord multi-organisation est exactement ce qu'il nous fallait pour piloter la performance ESG de nos 12 filiales.",
      author_role: "Directeur Développement Durable",
      company_description: "Groupe international",
      author_initials: "MB",
      rating: 5
    },
    {
      quote: "Mes clients apprécient les rapports PDF automatisés. Cela professionnalise nos accompagnements et fait gagner un temps précieux.",
      author_role: "Consultant RSE indépendant",
      company_description: "Cabinet conseil",
      author_initials: "AC",
      rating: 5
    }
  ];

  const useCases = [
    {
      title: "PME & ETI",
      icon: Building2,
      image: "https://images.unsplash.com/photo-1573879410945-d11e5319f6c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxzdGFydHVwJTIwdGVhbSUyMHdvcmtpbmclMjBtb2Rlcm4lMjBvZmZpY2UlMjBicmlnaHR8ZW58MHx8fHwxNzkxMzgyNDMxfDA&ixlib=rb-4.1.0&q=85",
      description: "Structurez votre démarche RSE et préparez-vous à la CSRD avec un outil adapté à vos ressources.",
      benefits: [
        "Analyse IA de chaque décision stratégique",
        "Suivi des objectifs RSE par trimestre",
        "Rapports PDF automatisés pour vos parties prenantes"
      ]
    },
    {
      title: "Grandes Entreprises",
      icon: Users,
      image: "https://images.unsplash.com/photo-1610374792793-f016b77ca51a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxleGVjdXRpdmUlMjBtZWV0aW5nJTIwZ2xhc3MlMjBvZmZpY2UlMjBza3lzY3JhcGVyfGVufDB8fHx8MTc5MTM4MjQzMXww&ixlib=rb-4.1.0&q=85",
      description: "Consolidez la performance ESG de vos filiales et pilotez votre stratégie groupe.",
      benefits: [
        "Tableau de bord multi-organisation",
        "Comparaison inter-filiales",
        "Export consolidé pour le reporting CSRD"
      ]
    },
    {
      title: "Cabinets de Conseil RSE",
      icon: Target,
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNvbnN1bHRhbnQlMjBhbmFseXRpY3MlMjBzY3JlZW4lMjBkYXNoYm9hcmR8ZW58MHx8fHwxNzkxMzgyNDMxfDA&ixlib=rb-4.1.0&q=85",
      description: "Accompagnez vos clients avec un outil professionnel d'aide à la décision.",
      benefits: [
        "Scoring ESG standardisé (E, S, G)",
        "Conformité aux normes ESRS",
        "Documentation des décisions pour audit"
      ]
    }
  ];

  const features = [
    {
      icon: Zap,
      title: "Analyse IA instantanée",
      description: "Notre IA GPT-5.2 qualifie vos décisions et calcule leur impact ESG en quelques secondes."
    },
    {
      icon: BarChart3,
      title: "Tableaux de bord ESG",
      description: "Visualisez votre progression Environnement, Social et Gouvernance avec des graphiques clairs."
    },
    {
      icon: Bell,
      title: "Alertes automatiques",
      description: "Recevez des notifications quand vos objectifs RSE sont à risque ou en retard."
    },
    {
      icon: FileText,
      title: "Exports PDF",
      description: "Générez des rapports professionnels pour vos décisions et votre progression CSRD."
    },
    {
      icon: Newspaper,
      title: "Veille RSE",
      description: "Restez informé avec des actualités RSE/CSRD en temps réel via des flux RSS."
    },
    {
      icon: Shield,
      title: "Sécurité & RGPD",
      description: "Vos données sont hébergées en France et traitées conformément au RGPD."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Leaf className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>VF Decide</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
            </Link>
            <Link to="/demo">
              <Button className="rounded-full">
                Demander une démo
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 hero-gradient">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Award className="w-4 h-4" />
            Cas d'Usage
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6" style={{ fontFamily: 'Manrope' }}>
            Comment utiliser
            <span className="text-gradient"> VF Decide</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Découvrez comment VF Decide peut accompagner votre organisation 
            dans sa démarche RSE et sa préparation à la directive CSRD.
          </p>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
              Adapté à votre organisation
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              VF Decide s'adapte à différents types d'organisations et de besoins
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <Card key={index} className="border-0 shadow-lg overflow-hidden" data-testid={`use-case-${index}`}>
                <div className="h-40 overflow-hidden">
                  <img 
                    src={useCase.image}
                    alt={useCase.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <useCase.icon className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                      {useCase.title}
                    </h3>
                  </div>
                  <p className="text-muted-foreground mb-4 text-sm">
                    {useCase.description}
                  </p>
                  <ul className="space-y-2">
                    {useCase.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-primary/5 to-emerald-500/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              <Quote className="w-4 h-4" />
              Retours d'expérience
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
              Ce qu'ils en disent
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Découvrez comment VF Decide accompagne des organisations de toutes tailles dans leur démarche RSE
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {displayTestimonials.map((testimonial, index) => (
              <Card key={index} className="border-0 shadow-lg bg-white/80 backdrop-blur" data-testid={`testimonial-${index}`}>
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating || 5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-foreground mb-6 italic leading-relaxed">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-semibold text-primary">{testimonial.author_initials || testimonial.initials}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground text-sm">{testimonial.author_role || testimonial.author}</p>
                      <p className="text-xs text-muted-foreground">{testimonial.company_description || testimonial.company}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Add Testimonial Button/Form */}
          <div className="text-center">
            {!showTestimonialForm ? (
              <Button 
                onClick={() => isLoggedIn ? setShowTestimonialForm(true) : window.location.href = '/login'}
                variant="outline" 
                className="rounded-full"
                data-testid="add-testimonial-btn"
              >
                <MessageSquarePlus className="w-4 h-4 mr-2" />
                {isLoggedIn ? "Partager votre expérience" : "Connectez-vous pour témoigner"}
              </Button>
            ) : (
              <Card className="max-w-xl mx-auto border-0 shadow-xl">
                <CardContent className="p-6">
                  {submitSuccess ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-foreground mb-2">Merci pour votre témoignage !</h3>
                      <p className="text-muted-foreground">Il sera publié après validation par notre équipe.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmitTestimonial} className="space-y-4">
                      <div className="text-left">
                        <h3 className="text-lg font-semibold text-foreground mb-1">Partagez votre expérience</h3>
                        <p className="text-sm text-muted-foreground">Votre témoignage sera publié après validation.</p>
                      </div>

                      <div className="space-y-2 text-left">
                        <Label htmlFor="quote">Votre témoignage *</Label>
                        <Textarea
                          id="quote"
                          placeholder="En quoi VF Decide vous a aidé dans votre démarche RSE..."
                          value={formData.quote}
                          onChange={(e) => setFormData({...formData, quote: e.target.value})}
                          required
                          rows={3}
                          data-testid="testimonial-quote-input"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2 text-left">
                          <Label htmlFor="author_role">Votre fonction *</Label>
                          <Input
                            id="author_role"
                            placeholder="Ex: Responsable RSE"
                            value={formData.author_role}
                            onChange={(e) => setFormData({...formData, author_role: e.target.value})}
                            required
                            data-testid="testimonial-role-input"
                          />
                        </div>
                        <div className="space-y-2 text-left">
                          <Label htmlFor="company_description">Votre organisation *</Label>
                          <Input
                            id="company_description"
                            placeholder="Ex: PME industrielle, 50 salariés"
                            value={formData.company_description}
                            onChange={(e) => setFormData({...formData, company_description: e.target.value})}
                            required
                            data-testid="testimonial-company-input"
                          />
                        </div>
                      </div>

                      <div className="space-y-2 text-left">
                        <Label>Note</Label>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setFormData({...formData, rating: star})}
                              className="focus:outline-none"
                            >
                              <Star 
                                className={`w-6 h-6 ${star <= formData.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
                              />
                            </button>
                          ))}
                        </div>
                      </div>

                      {submitError && (
                        <p className="text-sm text-red-500">{submitError}</p>
                      )}

                      <div className="flex gap-3 justify-end pt-2">
                        <Button 
                          type="button" 
                          variant="ghost" 
                          onClick={() => setShowTestimonialForm(false)}
                        >
                          Annuler
                        </Button>
                        <Button 
                          type="submit" 
                          disabled={isSubmitting}
                          data-testid="submit-testimonial-btn"
                        >
                          {isSubmitting ? (
                            "Envoi..."
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Envoyer
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
              Fonctionnalités clés
            </h2>
            <p className="text-muted-foreground">
              Tout ce dont vous avez besoin pour piloter votre stratégie RSE
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-md">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1" style={{ fontFamily: 'Manrope' }}>
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Prêt à découvrir VF Decide ?
          </h2>
          <p className="text-lg text-muted-foreground">
            Testez gratuitement notre plateforme ou demandez une démonstration personnalisée.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/demo">
              <Button size="lg" className="rounded-full px-8">
                <Calendar className="w-5 h-5 mr-2" />
                Demander une démo
              </Button>
            </Link>
            <Link to="/register">
              <Button size="lg" variant="outline" className="rounded-full px-8">
                Essai gratuit
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-card border-t border-border">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Leaf className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-semibold text-foreground">VF Decide</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary transition-colors">Accueil</Link>
            <Link to="/pricing" className="hover:text-primary transition-colors">Tarifs</Link>
            <Link to="/demo" className="hover:text-primary transition-colors">Démo</Link>
            <Link to="/mentions-legales" className="hover:text-primary transition-colors">Mentions légales</Link>
          </div>
          <p className="text-sm text-muted-foreground">© 2026 VF Decide - VF ACTIONS SAS</p>
        </div>
      </footer>
    </div>
  );
}
